import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useState, useEffect } from 'react';
import { GridLogicOperator, getGridDefaultColumnTypes, DEFAULT_GRID_COL_TYPE_KEY } from '@mui/x-data-grid-premium';
import { Reorder, useMotionValue, useDragControls } from 'framer-motion';
import { Flex, Body, EmptyState, Button, ConfigProvider, Input, DatePicker } from '@cognite/cogs-core';
import { CogsIllustration, AddIcon, DeleteIcon, DragHandleVerticalIcon, ArrowRightIcon } from '@cognite/cogs-icons';
import { convertGridNativeColTypeToSortNativeType } from '../data-grid/utils.js';
import { Option } from '../select/option.js';
import { Select } from '../select/select.js';
import { SelectPanel } from '../select-panel/select-panel.js';

var LOGIC_OPERATORS = [GridLogicOperator.And, GridLogicOperator.Or];
var FilterForm = function FilterForm(_ref) {
  var columns = _ref.columns,
    _ref$filterModel = _ref.filterModel,
    filterModel = _ref$filterModel === undefined ? {
      items: []
    } : _ref$filterModel,
    resultText = _ref.resultText,
    activeFilterState = _ref.activeFilterState,
    onFilterModelChange = _ref.onFilterModelChange;
  var addFilter = function addFilter() {
    var newFilter = {
      id: Date.now(),
      operator: '',
      field: activeFilterState ? activeFilterState.field : columns[0].value,
      value: null
    };
    onFilterModelChange(Object.assign(Object.assign({}, filterModel), {
      items: [].concat(_toConsumableArray(filterModel.items), [newFilter])
    }));
  };
  var removeFilter = function removeFilter(id) {
    var _a;
    onFilterModelChange(Object.assign(Object.assign({}, filterModel), {
      items: (_a = filterModel.items) === null || _a === undefined ? undefined : _a.filter(function (filter) {
        return filter.id !== id;
      })
    }));
  };
  var updateFilter = function updateFilter(id, updates) {
    var _a;
    onFilterModelChange(Object.assign(Object.assign({}, filterModel), {
      items: (_a = filterModel.items) === null || _a === undefined ? undefined : _a.map(function (filter) {
        return filter.id === id ? Object.assign(Object.assign({}, filter), updates) : filter;
      })
    }));
  };
  var onClearAll = function onClearAll() {
    onFilterModelChange(Object.assign(Object.assign({}, filterModel), {
      items: []
    }));
  };
  var filters = activeFilterState ? filterModel.items.filter(function (filter) {
    return filter.field == (activeFilterState === null || activeFilterState === undefined ? undefined : activeFilterState.field);
  }) : filterModel.items;
  var filterExists = filters && filters.length > 0;
  return jsxs(Flex, {
    className: "cogs-lab cogs-filterform",
    "data-testid": "cogs-filterform",
    direction: "column",
    gap: 8,
    children: [filterExists ? jsxs(Fragment, {
      children: [jsxs(Flex, {
        gap: 8,
        children: [filters.length > 1 && jsx("div", {
          className: "cogs-filterform__spacer"
        }), jsx(Body, {
          size: "medium",
          strong: true,
          children: "Shows fields where"
        })]
      }), filters.map(function (filter, index) {
        return jsx(FilterFormItem, {
          columns: columns,
          activeFilterState: activeFilterState,
          filter: filter,
          logicOperator: filterModel.logicOperator,
          onLogicOperatorChange: function onLogicOperatorChange(op) {
            return onFilterModelChange(Object.assign(Object.assign({}, filterModel), {
              logicOperator: op
            }));
          },
          filterIndex: index,
          filterLength: filters.length,
          onUpdate: function onUpdate(updates) {
            return filter.id && updateFilter(filter.id, updates);
          },
          onRemove: function onRemove() {
            return filter.id && removeFilter(filter.id);
          }
        }, filter.id);
      })]
    }) : jsx(Flex, {
      className: "cogs-lab cogs-filterform__empty-state-container",
      alignItems: "center",
      justifyContent: "center",
      children: jsx(EmptyState, {
        size: "small",
        illustration: jsx(CogsIllustration, {}),
        description: "No filter conditions applied"
      })
    }), jsxs(Flex, {
      gap: 8,
      className: "cogs-filterform__actions",
      children: [filters && filters.length > 1 && jsx("div", {
        className: "cogs-filterform__spacer"
      }), jsxs(Flex, {
        gap: 4,
        flex: 1,
        children: [jsx(Button, {
          onClick: addFilter,
          icon: jsx(AddIcon, {}),
          children: "Add filter"
        }), filterExists && jsx(Button, {
          type: "ghost",
          onClick: onClearAll,
          children: "Clear all"
        }), filterExists && resultText && jsx(Flex, {
          justifyContent: "flex-end",
          flex: 1,
          className: "cogs-filterform__result-container",
          alignItems: "center",
          children: jsx(Body, {
            size: "small",
            muted: true,
            children: resultText
          })
        })]
      })]
    })]
  });
};
var FilterFormItem = function FilterFormItem(_ref2) {
  var filter = _ref2.filter,
    filterLength = _ref2.filterLength,
    onUpdate = _ref2.onUpdate,
    _ref2$logicOperator = _ref2.logicOperator,
    logicOperator = _ref2$logicOperator === undefined ? GridLogicOperator.Or : _ref2$logicOperator,
    onLogicOperatorChange = _ref2.onLogicOperatorChange,
    filterIndex = _ref2.filterIndex,
    onRemove = _ref2.onRemove,
    activeFilterState = _ref2.activeFilterState,
    columns = _ref2.columns;
  var _a;
  var defaultColumnTypes = getGridDefaultColumnTypes();
  var currentColumn = columns.find(function (col) {
    return col.value === filter.field;
  });
  var filterOperators = defaultColumnTypes[(_a = currentColumn.type) !== null && _a !== undefined ? _a : DEFAULT_GRID_COL_TYPE_KEY].filterOperators;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    popperContainer = _useState2[0],
    setPopperContainer = _useState2[1];
  useEffect(function () {
    var panelContainer = document.querySelector('.MuiDataGrid-panel');
    if (!popperContainer && panelContainer) {
      setPopperContainer(panelContainer);
    }
  }, [popperContainer]);
  return jsx(ConfigProvider, {
    getOverlayContainer: function getOverlayContainer() {
      return popperContainer;
    },
    children: jsxs(Flex, {
      gap: 8,
      "data-testid": "cogs-filteritem",
      children: [!!filterIndex && filterIndex !== 0 && onLogicOperatorChange && jsx("div", {
        className: "cogs-filterform__spacer",
        children: jsx(Select, {
          "aria-label": "Logic operator",
          disabled: filterIndex > 1,
          fullWidth: true,
          value: logicOperator,
          options: LOGIC_OPERATORS.map(function (op) {
            return {
              label: op,
              value: op
            };
          }),
          onChange: function onChange(e, val) {
            return val && (onLogicOperatorChange === null || onLogicOperatorChange === undefined ? undefined : onLogicOperatorChange(val));
          }
        })
      }), !!filterLength && filterLength > 1 && filterIndex === 0 && jsx("div", {
        className: "cogs-filterform__spacer"
      }), !activeFilterState && jsx(Select, {
        "aria-label": "Field",
        value: filter.field,
        options: columns.map(function (item) {
          return {
            label: item.label || item.value,
            value: item.value
          };
        }),
        onChange: function onChange(e, val) {
          return val && onUpdate({
            field: val,
            value: null,
            operator: ''
          });
        }
      }), jsx(Select, {
        "aria-label": "Operator",
        value: filter.operator || '',
        options: filterOperators.map(function (op) {
          return {
            label: op.value,
            value: op.value
          };
        }),
        onChange: function onChange(e, val) {
          return val && onUpdate({
            operator: val
          });
        }
      }), currentColumn.type === 'string' && jsx(Input, {
        "aria-label": "".concat(filter.field, " value"),
        type: "text",
        value: filter.value,
        onChange: function onChange(e) {
          return onUpdate({
            value: e.target.value
          });
        }
      }), currentColumn.type === 'number' && jsx(Input, {
        "aria-label": "".concat(filter.field, " value"),
        type: "number",
        value: filter.value,
        onChange: function onChange(e) {
          return onUpdate({
            value: parseFloat(e.target.value)
          });
        }
      }), currentColumn.type === 'date' && jsx(DatePicker, {
        "aria-label": "".concat(filter.field, " value"),
        value: filter.value,
        onChange: function onChange(date) {
          return onUpdate({
            value: date
          });
        }
      }), currentColumn.type === 'singleSelect' && jsx(Select, {
        "aria-label": "".concat(filter.field, " value"),
        options: currentColumn.valueOptions,
        value: filter.value,
        onChange: function onChange(e, val) {
          return onUpdate({
            value: val
          });
        }
      }), jsx(Button, {
        type: "ghost",
        icon: jsx(DeleteIcon, {}),
        "aria-label": "Delete Filter",
        onClick: onRemove
      })]
    })
  });
};
var GroupByForm = function GroupByForm(_ref3) {
  var columns = _ref3.columns,
    items = _ref3.items,
    onItemChange = _ref3.onItemChange;
  var updateItem = function updateItem(id, updates) {
    onItemChange(items.map(function (item) {
      return item.id === id ? Object.assign(Object.assign({}, item), updates) : item;
    }));
  };
  var addGroupBy = function addGroupBy() {
    var newGroupBy = {
      id: Date.now().toString(),
      value: ''
    };
    onItemChange([].concat(_toConsumableArray(items), [newGroupBy]));
  };
  var removeGroupBy = function removeGroupBy(id) {
    onItemChange(items.filter(function (item) {
      return item.id !== id;
    }));
  };
  var getAvailableColumns = function getAvailableColumns(item) {
    var availableColumns = columns.filter(function (col) {
      return !items.some(function (selectedItem) {
        return selectedItem.value === col.value && selectedItem.id !== item.id;
      });
    });
    var selectedColumn = columns.find(function (col) {
      return col.value === item.value;
    });
    if (selectedColumn && !availableColumns.includes(selectedColumn)) {
      availableColumns.push(selectedColumn);
    }
    return availableColumns;
  };
  return jsxs(Flex, {
    className: "cogs-lab cogs-groupby",
    "data-testid": "cogs-groupby",
    direction: "column",
    gap: 8,
    children: [items.length > 0 ? jsxs(Fragment, {
      children: [jsxs(Flex, {
        gap: 8,
        children: [jsx("div", {
          className: "cogs-groupby__spacer"
        }), jsx(Body, {
          size: "medium",
          strong: true,
          children: "Group by"
        })]
      }), jsx(Reorder.Group, {
        className: "cogs-groupby__group-item__container",
        as: "div",
        axis: "y",
        onReorder: onItemChange,
        values: items,
        children: items.map(function (item, index) {
          return jsx(GroupByItem, {
            columns: getAvailableColumns(item),
            item: item,
            onUpdate: function onUpdate(updates) {
              updateItem(item.id, updates);
            },
            onRemove: function onRemove() {
              return removeGroupBy(item.id);
            }
          }, item.id);
        })
      })]
    }) : jsx(Flex, {
      className: "cogs-lab cogs-groupby__empty-state-container",
      alignItems: "center",
      justifyContent: "center",
      children: jsx(EmptyState, {
        size: "small",
        illustration: jsx(CogsIllustration, {}),
        description: "No group conditions applied"
      })
    }), jsxs(Flex, {
      gap: 4,
      className: "cogs-groupby__actions",
      children: [jsxs(Flex, {
        gap: 8,
        children: [items.length > 0 && jsx("div", {
          className: "cogs-groupby__spacer"
        }), items.length !== columns.length && jsx(Button, {
          onClick: addGroupBy,
          icon: jsx(AddIcon, {}),
          children: "Add group"
        })]
      }), items.length > 0 && jsx(Button, {
        type: "ghost",
        onClick: function onClick() {
          return onItemChange([]);
        },
        children: "Clear all"
      })]
    })]
  });
};
var GroupByItem = function GroupByItem(_ref4) {
  var columns = _ref4.columns,
    item = _ref4.item,
    onRemove = _ref4.onRemove,
    onUpdate = _ref4.onUpdate;
  var speedY = useMotionValue(0);
  var dragControls = useDragControls();
  return jsxs(Reorder.Item, {
    className: "cogs-groupby__group-item",
    "data-testid": "cogs-groupby-item",
    style: {
      y: speedY
    },
    as: "div",
    value: item,
    dragControls: dragControls,
    id: item.value,
    dragListener: false,
    children: [jsx(DragHandleVerticalIcon, {
      className: "cogs-groupby__reorder-icon",
      onPointerDown: function onPointerDown(evt) {
        return dragControls.start(evt);
      }
    }), jsx(Select, {
      "aria-label": "Field",
      options: columns.map(function (item) {
        return {
          label: item.label || item.value,
          value: item.value
        };
      }),
      value: item.value,
      onChange: function onChange(e, val) {
        return val && onUpdate({
          value: val
        });
      }
    }), jsx(Button, {
      type: "ghost",
      icon: jsx(DeleteIcon, {}),
      "aria-label": "Delete group",
      onClick: onRemove
    })]
  });
};
var SortForm = function SortForm(_ref5) {
  var columns = _ref5.columns,
    items = _ref5.items,
    onItemChange = _ref5.onItemChange;
  var updateItem = function updateItem(id, updates) {
    onItemChange(items.map(function (item) {
      return item.id === id ? Object.assign(Object.assign({}, item), updates) : item;
    }));
  };
  var addItem = function addItem() {
    var newItem = {
      id: Date.now(),
      field: '',
      sort: undefined
    };
    onItemChange([].concat(_toConsumableArray(items), [newItem]));
  };
  var removeItem = function removeItem(id) {
    onItemChange(items.filter(function (filter) {
      return filter.id !== id;
    }));
  };
  var getAvailableColumns = function getAvailableColumns(item) {
    var availableColumns = columns.filter(function (col) {
      return !items.some(function (selectedItem) {
        return selectedItem.field === col.value && selectedItem.id !== item.id;
      });
    });
    var selectedColumn = columns.find(function (col) {
      return col.value === item.sort;
    });
    if (selectedColumn && !availableColumns.includes(selectedColumn)) {
      availableColumns.push(selectedColumn);
    }
    return availableColumns;
  };
  return jsxs(Flex, {
    className: "cogs-lab cogs-sortby",
    "data-testid": "cogs-sortby",
    direction: "column",
    gap: 8,
    children: [items.length ? jsxs(Fragment, {
      children: [jsxs(Flex, {
        gap: 8,
        children: [jsx("div", {
          className: "cogs-groupby__spacer"
        }), jsx(Body, {
          size: "medium",
          strong: true,
          children: "Sort by"
        })]
      }), jsx(Reorder.Group, {
        as: "div",
        className: "cogs-groupby__group-item__container",
        axis: "y",
        onReorder: onItemChange,
        values: items,
        children: items.map(function (item, index) {
          return jsx(SortItem, {
            columns: getAvailableColumns(item),
            item: item,
            onUpdate: function onUpdate(updates) {
              updateItem(item.id, updates);
            },
            onRemove: function onRemove() {
              return removeItem(item.id);
            }
          }, item.id);
        })
      })]
    }) : jsx(Flex, {
      className: "cogs-lab cogs-filterform__empty-state-container",
      alignItems: "center",
      justifyContent: "center",
      children: jsx(EmptyState, {
        size: "small",
        illustration: jsx(CogsIllustration, {}),
        description: "No sorting conditions applied"
      })
    }), jsxs(Flex, {
      gap: 4,
      className: "cogs-groupby__actions",
      children: [jsxs(Flex, {
        gap: 8,
        children: [items.length > 0 && jsx("div", {
          className: "cogs-groupby__spacer"
        }), items.length !== columns.length && jsx(Button, {
          onClick: addItem,
          icon: jsx(AddIcon, {}),
          children: "Add sort"
        })]
      }), items.length > 0 && jsx(Button, {
        type: "ghost",
        onClick: function onClick() {
          return onItemChange([]);
        },
        children: "Clear all"
      })]
    })]
  });
};
var SortItem = function SortItem(_ref6) {
  var columns = _ref6.columns,
    item = _ref6.item,
    onUpdate = _ref6.onUpdate,
    onRemove = _ref6.onRemove;
  var speedY = useMotionValue(0);
  var currentColumn = columns.find(function (col) {
    return col.value === item.field;
  });
  var dragControls = useDragControls();
  return jsxs(Reorder.Item, {
    as: "div",
    className: "cogs-groupby__group-item",
    "data-testid": "cogs-sortby-item",
    style: {
      y: speedY
    },
    value: item,
    dragControls: dragControls,
    id: "".concat(item.id).concat(item.sort),
    dragListener: false,
    children: [jsx(DragHandleVerticalIcon, {
      "data-testid": "drag-handle",
      className: "cogs-groupby__reorder-icon",
      onPointerDown: function onPointerDown(evt) {
        return dragControls.start(evt);
      }
    }), jsx(Select, {
      "aria-label": "Field",
      options: columns.map(function (item) {
        return {
          label: item.label || item.value,
          value: item.value
        };
      }),
      value: item.field,
      onChange: function onChange(e, val) {
        return val && onUpdate({
          field: val
        });
      }
    }), jsx(Select, {
      "aria-label": "Sort order",
      value: item.sort,
      renderValue: function renderValue(value) {
        if (value && typeof value.label == 'string') {
          var _value$label$split = value.label.split('-'),
            _value$label$split2 = _slicedToArray(_value$label$split, 2),
            leftOrder = _value$label$split2[0],
            rightOrder = _value$label$split2[1];
          return jsxs(Flex, {
            alignItems: "center",
            gap: 8,
            children: [jsx("div", {
              children: leftOrder
            }), jsx(ArrowRightIcon, {}), jsx("div", {
              children: rightOrder
            })]
          });
        }
        return '';
      },
      onChange: function onChange(e, val) {
        return val && onUpdate({
          sort: val
        });
      },
      children: sortType[convertGridNativeColTypeToSortNativeType(currentColumn === null || currentColumn === undefined ? undefined : currentColumn.type)].map(function (sort) {
        var _sort$label$split = sort.label.split('-'),
          _sort$label$split2 = _slicedToArray(_sort$label$split, 2),
          leftOrder = _sort$label$split2[0],
          rightOrder = _sort$label$split2[1];
        return jsx(Option, {
          label: sort.label,
          value: sort.value,
          children: jsxs(Flex, {
            alignItems: "center",
            gap: 8,
            children: [jsx("div", {
              children: leftOrder
            }), jsx(ArrowRightIcon, {}), jsx("div", {
              children: rightOrder
            })]
          })
        }, sort.value);
      })
    }), jsx(Button, {
      type: "ghost",
      icon: jsx(DeleteIcon, {}),
      "aria-label": "Remove Sort Item",
      onClick: onRemove
    })]
  });
};
var sortType = {
  string: [{
    label: 'A-Z',
    value: 'asc'
  }, {
    label: 'Z-A',
    value: 'desc'
  }],
  number: [{
    label: '1-9',
    value: 'asc'
  }, {
    label: '9-1',
    value: 'desc'
  }],
  date: [{
    label: 'Oldest-Latest',
    value: 'desc'
  }, {
    label: 'Latest-Oldest',
    value: 'asc'
  }],
  singleSelect: [{
    label: 'First-Last',
    value: 'asc'
  }, {
    label: 'Last-First',
    value: 'desc'
  }],
  dateTime: [{
    label: 'Oldest-Latest',
    value: 'desc'
  }, {
    label: 'Latest-Oldest',
    value: 'asc'
  }]
};
var ReorderableCheckList = function ReorderableCheckList(_ref7) {
  var icon = _ref7.icon,
    triggerLabel = _ref7.triggerLabel,
    title = _ref7.title,
    description = _ref7.description,
    _ref7$items = _ref7.items,
    items = _ref7$items === undefined ? [] : _ref7$items,
    onColumnVisibleChange = _ref7.onColumnVisibleChange,
    onSelectAllChange = _ref7.onSelectAllChange,
    onItemChange = _ref7.onItemChange,
    reorderable = _ref7.reorderable,
    searchable = _ref7.searchable;
  var checkedItemCount = items.filter(function (item) {
    return item && item.checked;
  }).length;
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    search = _useState4[0],
    setSearch = _useState4[1];
  var allItemsChecked = items.length === checkedItemCount;
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isManageColumnVisibleOpen = _useState6[0],
    setIsManageColumnVisibleOpen = _useState6[1];
  var atleastSomeItemsChecked = checkedItemCount > 0;
  var onlySomeItemsNotAllChecked = atleastSomeItemsChecked && !allItemsChecked;
  var handleAllColumnVisibleChange = function handleAllColumnVisibleChange(items) {
    if (onlySomeItemsNotAllChecked || !allItemsChecked) {
      var newVisibilityModel = items.reduce(function (model, item) {
        model[item.field] = true; // Set each column's visibility to true
        return model;
      }, {});
      onSelectAllChange === null || onSelectAllChange === undefined ? undefined : onSelectAllChange(newVisibilityModel);
      onItemChange === null || onItemChange === undefined ? undefined : onItemChange(items.map(function (item) {
        return Object.assign(Object.assign({}, item), {
          checked: true
        });
      }));
    } else {
      // Have to go with this approach when hidding and showing all columns
      var _newVisibilityModel = items.reduce(function (model, item) {
        model[item.field] = false; // Set each column's visibility to false
        return model;
      }, {});
      onSelectAllChange === null || onSelectAllChange === undefined ? undefined : onSelectAllChange(_newVisibilityModel);
      onItemChange === null || onItemChange === undefined ? undefined : onItemChange(items.map(function (item) {
        return Object.assign(Object.assign({}, item), {
          checked: false
        });
      }));
    }
  };
  return jsxs(SelectPanel, {
    onShow: function onShow() {
      return setIsManageColumnVisibleOpen(true);
    },
    onHide: function onHide() {
      return setIsManageColumnVisibleOpen(false);
    },
    visible: isManageColumnVisibleOpen,
    children: [jsx(SelectPanel.Trigger, {
      children: jsx(Button, {
        size: "small",
        toggled: isManageColumnVisibleOpen,
        onClick: function onClick() {
          return setIsManageColumnVisibleOpen(function (prev) {
            return !prev;
          });
        },
        type: "ghost",
        icon: icon,
        children: triggerLabel
      })
    }), jsxs(SelectPanel.Header, {
      title: title,
      description: description,
      children: [searchable && jsx(SelectPanel.SearchBar, {
        onChange: function onChange(e) {
          return setSearch(e.target.value);
        },
        value: search
      }), onSelectAllChange && jsx(SelectPanel.SelectAll, {
        label: "Select all",
        indeterminate: onlySomeItemsNotAllChecked,
        checked: atleastSomeItemsChecked,
        onChange: function onChange(_) {
          handleAllColumnVisibleChange(items);
        }
      })]
    }), jsx(SelectPanel.Body, {
      children: jsx(SelectPanel.Section, {
        items: items,
        onReorder: onItemChange,
        children: items.map(function (column) {
          if (search && !column.field.toLowerCase().includes(search.toLowerCase())) return null;
          return jsx(SelectPanel.Item, {
            item: column,
            label: column.headerName || column.field,
            checked: column.checked,
            onActiveChange: function onActiveChange(visible) {
              return onColumnVisibleChange === null || onColumnVisibleChange === undefined ? undefined : onColumnVisibleChange(column.field, visible);
            },
            reorderable: reorderable,
            variant: "checkbox"
          }, column.field);
        })
      })
    })]
  });
};

export { FilterForm, FilterFormItem, GroupByForm, GroupByItem, ReorderableCheckList, SortForm, SortItem };
